import React, { useEffect, useState, useLayoutEffect, Suspense } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDarkMode } from '../../context/DarkModeContext';
import { useToast } from '../../context/ToastContext';
import { supabase } from '../../utils/supabase';
import { format } from 'date-fns';
import { LoadingSpinner } from '../LoadingSpinner';
import { ErrorBoundary } from '../ErrorBoundary';
import { IoArrowBack, IoCalendarOutline, IoTimeOutline, IoShareOutline, IoDownloadOutline, IoTrashOutline, IoPencilOutline, IoSaveOutline, IoMenuOutline } from 'react-icons/io5';
import { Modal } from '../Modal';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { FRAMEWORK_TEMPLATES, type FrameworkSection } from '../../constants/retroFrameworks';
import { RetroSidePanel } from './SidePanel';
import {
  DndContext,
  DragOverlay,
  useDroppable,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import { useAuthState } from '../../hooks/useAuthState';
import MarkdownEditor from '../RetroCreator/ReflectionEditor/MarkdownEditorWrapper';

// Import markdown editor styles
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import '../../styles/markdown-editor.css';

// Create a client-only wrapper for MDEditor
const ClientOnly = ({ children, fallback = null }: { children: React.ReactNode, fallback?: React.ReactNode }) => {
  const [isClient, setIsClient] = useState(false);
  
  // Use useLayoutEffect to avoid state updates during render
  // This will run before the browser paints, avoiding the warning
  useLayoutEffect(() => {
    setIsClient(true);
  }, []);
  
  return isClient ? <>{children}</> : <>{fallback}</>;
};

interface LinkedContent {
  id: string;
  type: 'note' | 'career_card';
  title: string;
  preview: string;
  created_at: string;
}

interface RetroData {
  id: string;
  title: string;
  description: string;
  created_at: string;
  user_id: string;
  framework: string;
  start_date: string;
  end_date: string;
  framework_type?: string;
}

interface RetroEntry {
  id: string;
  retrospective_id: string;
  content: string;
  category: string;
  created_at: string;
  metadata?: {
    referencedContent?: {
      id: string;
      type: 'note' | 'card';
    }[];
  };
}

interface DraggedItemData {
  type: string;
  cardId: string;
  title: string;
  preview?: string;
}

interface RetroViewerProps {
  isEditMode: boolean;
  onEditModeChange: (isEditMode: boolean) => void;
}

const LinkedContentCard: React.FC<{ content: LinkedContent }> = ({ content }) => {
  const { darkMode } = useDarkMode();
  
  return (
    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {content.title}
        </h3>
        <span className={`text-sm px-2 py-0.5 rounded-full ${
          content.type === 'note'
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
        }`}>
          {content.type === 'note' ? 'Note' : 'Career Card'}
        </span>
      </div>
      <p className={`text-sm line-clamp-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {content.preview}
      </p>
      <div className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Created {format(new Date(content.created_at), 'MMM d, yyyy')}
      </div>
    </div>
  );
};

interface QuickAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  className?: string;
}

const QuickActionButton: React.FC<QuickAction> = ({ label, icon, onClick, className }) => {
  const { darkMode } = useDarkMode();
  
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-md transition-colors duration-200 ${
        darkMode 
          ? 'hover:bg-gray-700 text-gray-300 hover:text-gray-200' 
          : 'hover:bg-gray-100 text-gray-600 hover:text-gray-800'
      } ${className}`}
      title={label}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
};

interface DroppableSectionProps {
  children: React.ReactNode;
  id: string;
  darkMode: boolean;
  isInvalid?: boolean;
}

const DroppableSection: React.FC<DroppableSectionProps> = ({ children, id, darkMode, isInvalid }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `section-${id}`,
    data: {
      type: 'section',
      sectionId: id
    }
  });

  return (
    <div
      ref={setNodeRef}
      className={`transition-colors duration-200 ${
        isOver && !isInvalid
          ? (darkMode ? 'bg-blue-900/20 ring-2 ring-blue-500' : 'bg-blue-50 ring-2 ring-blue-500')
          : isOver && isInvalid
          ? (darkMode ? 'bg-red-900/20 ring-2 ring-red-500' : 'bg-red-50 ring-2 ring-red-500')
          : ''
      }`}
    >
      {children}
    </div>
  );
};

export const RetroViewer = React.forwardRef<
  { handleEditModeToggle: () => void; handleSaveChanges: () => void },
  RetroViewerProps
>(({ isEditMode, onEditModeChange }, ref) => {
  const { retroId } = useParams<{ retroId: string }>();
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retro, setRetro] = useState<RetroData | null>(null);
  const [entries, setEntries] = useState<RetroEntry[]>([]);
  const [linkedContent, setLinkedContent] = useState<LinkedContent[]>([]);
  const [activeContentId, setActiveContentId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const mainContentRef = React.useRef<HTMLDivElement>(null);
  const [editedEntries, setEditedEntries] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedStartDate, setEditedStartDate] = useState('');
  const [editedEndDate, setEditedEndDate] = useState('');
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [activeDragItem, setActiveDragItem] = useState<DraggedItemData | null>(null);
  const [invalidDropTargets, setInvalidDropTargets] = useState<Set<string>>(new Set());
  const [isDropSuccessful, setIsDropSuccessful] = useState(false);
  const { user, isAuthenticated } = useAuthState();

  useLayoutEffect(() => {
    if (isEditMode && retro) {
      // Immediately scroll to top before any reflow occurs
      if (mainContentRef.current) {
        mainContentRef.current.scrollTop = 0;
      }

      // Initialize edit mode values
      setEditedTitle(retro.title);
      setEditedDescription(retro.description || '');
      setEditedStartDate(retro.start_date.split('T')[0]);
      setEditedEndDate(retro.end_date.split('T')[0]);
      
      // Initialize entry content
      const initialEntries = entries.reduce((acc, entry) => {
        acc[entry.id] = entry.content || '';
        return acc;
      }, {} as Record<string, string>);
      setEditedEntries(initialEntries);

      // Ensure we stay at top even after editors mount
      const timeoutId = setTimeout(() => {
        requestAnimationFrame(() => {
          if (mainContentRef.current) {
            mainContentRef.current.scrollTop = 0;
          }
        });
      }, 0);

      return () => clearTimeout(timeoutId);
    } else {
      // Reset values when leaving edit mode
      setEditedEntries({});
      setEditedTitle('');
      setEditedDescription('');
      setEditedStartDate('');
      setEditedEndDate('');
    }
  }, [isEditMode, retro, entries]);

  const handleEditModeToggle = () => {
    if (isEditMode) {
      // If leaving edit mode without saving, reset changes
      setEditedEntries({});
      setEditedTitle('');
      setEditedDescription('');
      setEditedStartDate('');
      setEditedEndDate('');
    } else if (retro) {
      // Entering edit mode - set initial values
      setEditedTitle(retro.title);
      setEditedDescription(retro.description || '');
      setEditedStartDate(retro.start_date.split('T')[0]);
      setEditedEndDate(retro.end_date.split('T')[0]);
      
      // Initialize editedEntries with all existing entries
      const initialEntries = entries.reduce((acc, entry) => {
        acc[entry.id] = entry.content || '';
        return acc;
      }, {} as Record<string, string>);
      setEditedEntries(initialEntries);
    }
    onEditModeChange(!isEditMode);
  };

  const handleSaveChanges = async () => {
    if (!retroId || !retro) return;
    
    if (!user) {
      showToast('You need to be logged in to save changes', 'error');
      return;
    }

    try {
      setIsSaving(true);
      
      // Check if any retro details have changed
      const hasRetroChanges = editedTitle !== retro.title || 
        editedDescription !== retro.description ||
        editedStartDate !== retro.start_date.split('T')[0] ||
        editedEndDate !== retro.end_date.split('T')[0];

      if (hasRetroChanges) {
        const { error: retroError } = await supabase
          .from('retrospectives')
          .update({
            title: editedTitle,
            description: editedDescription,
            start_date: editedStartDate,
            end_date: editedEndDate
          })
          .eq('id', retroId);

        if (retroError) throw retroError;

        // Update local state
        setRetro(prev => prev ? {
          ...prev,
          title: editedTitle,
          description: editedDescription,
          start_date: editedStartDate,
          end_date: editedEndDate
        } : null);
      }

      // Handle both new and existing entries
      if (Object.keys(editedEntries).length > 0) {
        const updates = await Promise.all(
          Object.entries(editedEntries).map(async ([entryId, content]) => {
            // If this is a new entry (ID starts with 'new-')
            if (entryId.startsWith('new-')) {
              const category = entryId.replace('new-', '');
              const { data, error } = await supabase
                .from('retrospective_entries')
                .insert({
                  retrospective_id: retroId,
                  content: content,
                  category: category
                })
                .select()
                .single();

              if (error) throw error;
              return { id: entryId, newEntry: data };
            } else {
              // Existing entry update
              const { error } = await supabase
                .from('retrospective_entries')
                .update({ content })
                .eq('id', entryId);

              if (error) throw error;
              return { id: entryId, content };
            }
          })
        );

        // Update local state
        setEntries(prev => {
          const newEntries = [...prev];
          updates.forEach(update => {
            if ('newEntry' in update && update.newEntry) {
              // Remove temporary entry and add the new one
              const index = newEntries.findIndex(e => e.id === update.id);
              if (index !== -1) {
                newEntries[index] = update.newEntry;
              } else {
                newEntries.push(update.newEntry);
              }
            } else {
              // Update existing entry
              const index = newEntries.findIndex(e => e.id === update.id);
              if (index !== -1) {
                newEntries[index] = {
                  ...newEntries[index],
                  content: editedEntries[update.id]
                };
              }
            }
          });
          return newEntries;
        });
      }

      setEditedEntries({});
      setEditedTitle('');
      setEditedDescription('');
      setEditedStartDate('');
      setEditedEndDate('');
      onEditModeChange(false);
      showToast('Changes saved successfully', 'success');
    } catch (error) {
      console.error('Error saving changes:', error);
      showToast('Failed to save changes', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  React.useImperativeHandle(ref, () => ({
    handleEditModeToggle,
    handleSaveChanges
  }));

  useEffect(() => {
    const loadRetroData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // First load the retrospective details
        const { data: retroData, error: retroError } = await supabase
          .from('retrospectives')
          .select('*')
          .eq('id', retroId)
          .single();

        if (retroError) throw retroError;
        setRetro(retroData);

        // Load retrospective entries
        const { data: entriesData, error: entriesError } = await supabase
          .from('retrospective_entries')
          .select('*')
          .eq('retrospective_id', retroId)
          .order('created_at', { ascending: true });

        if (entriesError) throw entriesError;

        // Load entry-level content links
        const { data: entryLinksData, error: entryLinksError } = await supabase
          .from('content_links')
          .select(`
            id,
            source_id,
            target_id,
            target_type,
            metadata
          `)
          .eq('source_type', 'retrospective_entry')
          .in('source_id', entriesData.map(e => e.id));

        if (entryLinksError) throw entryLinksError;

        // Map entry links to their respective entries
        const entriesWithLinks = entriesData.map(entry => {
          const entryLinks = entryLinksData.filter(link => link.source_id === entry.id);
          return {
            ...entry,
            metadata: {
              ...entry.metadata,
              referencedContent: entryLinks.map(link => ({
                id: link.target_id,
                type: link.target_type === 'career_card' ? 'card' : 'note'
              }))
            }
          };
        });

        setEntries(entriesWithLinks);

        // Load retrospective-level content links for the Referenced Content section
        const { data: retroLinksData, error: retroLinksError } = await supabase
          .from('content_links')
          .select(`
            id,
            target_id,
            target_type,
            metadata
          `)
          .match({
            source_id: retroId,
            source_type: 'retrospective'
          });

        if (retroLinksError) throw retroLinksError;

        // If we have linked content, load the actual content
        if (retroLinksData && retroLinksData.length > 0) {
          const noteIds = retroLinksData.filter(link => link.target_type === 'note').map(link => link.target_id);
          const cardIds = retroLinksData.filter(link => link.target_type === 'career_card').map(link => link.target_id);

          const [notesResponse, cardsResponse] = await Promise.all([
            // Load linked notes
            noteIds.length > 0 ? supabase
              .from('notes')
              .select('id, title, content, created_at')
              .in('id', noteIds) : Promise.resolve({ data: [], error: null }),

            // Load linked career cards
            cardIds.length > 0 ? supabase
              .from('career_cards')
              .select('cardid:id, card_data->title, card_data->description, created_at')
              .in('id', cardIds) : Promise.resolve({ data: [], error: null })
          ]);

          if (notesResponse.error) throw notesResponse.error;
          if (cardsResponse.error) throw cardsResponse.error;

          // Transform the data into LinkedContent format
          const linkedContentItems: LinkedContent[] = [
            ...(notesResponse.data || []).map(note => ({
              id: note.id,
              type: 'note' as const,
              title: String(note.title || 'Untitled Note'),
              preview: String(note.content || ''),
              created_at: note.created_at
            })),
            ...(cardsResponse.data || []).map(card => ({
              id: card.cardid,
              type: 'career_card' as const,
              title: String(card.title || 'Untitled Card'),
              preview: String(card.description || ''),
              created_at: card.created_at
            }))
          ];

          setLinkedContent(linkedContentItems);
        }

      } catch (error) {
        console.error('Error loading retro data:', error);
        setError('Failed to load retrospective data. Please try again later.');
        showToast('Failed to load retrospective data', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (retroId) {
      loadRetroData();
    }
  }, [retroId, showToast]);

  const handleDelete = async () => {
    if (!retroId) return;
    
    try {
      setIsLoading(true);
      
      // Delete content links first
      const { error: linksError } = await supabase
        .from('content_links')
        .delete()
        .eq('source_id', retroId)
        .eq('source_type', 'retrospective');

      if (linksError) throw linksError;

      // Delete entries
      const { error: entriesError } = await supabase
        .from('retrospective_entries')
        .delete()
        .eq('retrospective_id', retroId);

      if (entriesError) throw entriesError;

      // Delete the retrospective
      const { error: retroError } = await supabase
        .from('retrospectives')
        .delete()
        .eq('id', retroId);

      if (retroError) throw retroError;

      showToast('Retrospective deleted successfully', 'success');
      navigate('/retrospectives');
    } catch (error) {
      console.error('Error deleting retrospective:', error);
      showToast('Failed to delete retrospective', 'error');
    } finally {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleExport = async () => {
    if (!contentRef.current || !retro) return;
    
    try {
      setIsExporting(true);
      showToast('Preparing export...', 'info');

      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
      });

      // PDF dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margins = { top: 20, bottom: 20 };

      // Add title
      pdf.setFontSize(20);
      pdf.text(retro.title, pdfWidth / 2, margins.top, { align: 'center' });

      // Add metadata
      pdf.setFontSize(10);
      const metaText = [
        `Created: ${format(new Date(retro.created_at), 'MMM d, yyyy')}`,
        `Period: ${format(new Date(retro.start_date), 'MMM d, yyyy')} - ${format(new Date(retro.end_date), 'MMM d, yyyy')}`,
        `Framework: ${(retro.framework_type || 'custom').split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}`
      ];
      pdf.text(metaText, pdfWidth / 2, margins.top + 10, { align: 'center' });

      let currentY = margins.top + 30;

      // Process each section
      for (const section of frameworkLayout.sections) {
        const sectionEntries = entries.filter(entry => entry.category === section.id);
        if (sectionEntries.length === 0) continue;

        // Add section header
        if (currentY > pdfHeight - margins.bottom - 20) {
          pdf.addPage();
          currentY = margins.top;
        }

        pdf.setFontSize(16);
        pdf.text(section.label, 20, currentY);
        currentY += 10;

        // Process each entry
        for (const entry of sectionEntries) {
          try {
            // Create a temporary div for the entry content
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = entry.content;
            tempDiv.className = 'prose max-w-none p-4';
            document.body.appendChild(tempDiv);

            // Capture the entry content
            const canvas = await html2canvas(tempDiv, {
              scale: 2,
              logging: false,
              useCORS: true,
              backgroundColor: null
            });

            document.body.removeChild(tempDiv);

            // Calculate dimensions
            const imgWidth = pdfWidth - 40;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Add new page if needed
            if (currentY + imgHeight > pdfHeight - margins.bottom) {
              pdf.addPage();
              currentY = margins.top;
            }

            // Add the image
            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            pdf.addImage(imgData, 'JPEG', 20, currentY, imgWidth, imgHeight);
            currentY += imgHeight + 10;

            // Add referenced content if any
            if (entry.metadata?.referencedContent?.length) {
              pdf.setFontSize(10);
              pdf.text('Referenced content:', 20, currentY);
              currentY += 5;

              const refs = entry.metadata.referencedContent
                .map(ref => {
                  const content = linkedContent.find(c => c.id === ref.id);
                  return content ? `• ${content.title} (${ref.type})` : null;
                })
                .filter(Boolean);

              refs.forEach(ref => {
                if (currentY > pdfHeight - margins.bottom - 5) {
                  pdf.addPage();
                  currentY = margins.top;
                }
                pdf.text(ref!, 25, currentY);
                currentY += 5;
              });

              currentY += 5;
            }
          } catch (error) {
            console.error('Error processing entry:', error);
            // Continue with next entry
          }
        }
      }

      // Save the PDF
      pdf.save(`${retro.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_retrospective.pdf`);
      showToast('Export completed successfully', 'success');
    } catch (error) {
      console.error('Error exporting retrospective:', error);
      showToast('Failed to export retrospective. Please try again.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  const handleCopyLink = async () => {
    const shareUrl = `${window.location.origin}/retrospectives/${retroId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast('Link copied to clipboard', 'success');
    } catch (error) {
      console.error('Error copying link:', error);
      showToast('Failed to copy link', 'error');
    }
    setIsShareModalOpen(false);
  };

  const handleContentClick = (contentId: string) => {
    setActiveContentId(activeContentId === contentId ? null : contentId);
  };

  const handleEntryEdit = (entryId: string, content: string) => {
    setEditedEntries(prev => ({
      ...prev,
      [entryId]: content
    }));
  };

  const handleDragStart = async (event: DragStartEvent) => {
    const { active } = event;
    const draggedItem = active.data.current as DraggedItemData;
    setActiveDragItem(draggedItem);
    setIsDropSuccessful(false);

    try {
      // Get all existing entry-level links for this item in this retrospective
      const { data: existingLinks, error: linksError } = await supabase
        .from('content_links')
        .select('metadata')
        .match({
          target_id: draggedItem.cardId,
          target_type: draggedItem.type === 'career_card' ? 'career_card' : 'note',
          source_type: 'retrospective_entry'
        })
        .contains('metadata', { retrospective_id: retroId });

      if (linksError) throw linksError;

      // Create a set of sections that already have this item
      const invalidTargets = new Set<string>();
      existingLinks?.forEach(link => {
        if (link.metadata?.section_id) {
          invalidTargets.add(link.metadata.section_id);
        }
      });
      
      setInvalidDropTargets(invalidTargets);
    } catch (error) {
      console.error('Error checking existing links:', error);
      // If there's an error, we'll be conservative and not mark any targets as invalid
      setInvalidDropTargets(new Set());
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragItem(null); // Immediately remove the drag overlay

    if (!over) {
      setInvalidDropTargets(new Set());
      return;
    }

    const draggedItemData = active.data.current as DraggedItemData;
    const dropTargetId = (over.id as string).replace('section-', '');

    // Check if this is an invalid drop target
    if (invalidDropTargets.has(dropTargetId)) {
      setInvalidDropTargets(new Set());
      return;
    }

    try {
      setIsDropSuccessful(true);  // Set this first to prevent the return animation

      // No longer need to fetch the user here
      if (!isAuthenticated || !user) {
        throw new Error('Failed to get current user');
      }

      // Get the entry for this section
      const entryToUpdate = entries.find(entry => entry.category === dropTargetId);
      if (!entryToUpdate) return;

      const targetType = draggedItemData.type === 'career_card' ? 'career_card' : 'note';

      // Create entry-level link
      const { error: entryLinkError } = await supabase
        .from('content_links')
        .insert({
          source_id: entryToUpdate.id,
          source_type: 'retrospective_entry',
          target_id: draggedItemData.cardId,
          target_type: targetType,
          user_id: user.id,
          metadata: {
            section_id: dropTargetId,
            retrospective_id: retroId
          }
        });

      if (entryLinkError) throw entryLinkError;

      // Update local entries state immediately
      setEntries(prev => prev.map(entry => {
        if (entry.id === entryToUpdate.id) {
          return {
            ...entry,
            metadata: {
              ...entry.metadata,
              referencedContent: [
                ...(entry.metadata?.referencedContent || []),
                {
                  id: draggedItemData.cardId,
                  type: targetType === 'career_card' ? 'card' : 'note'
                }
              ]
            }
          };
        }
        return entry;
      }));

      // Get existing retrospective-level link
      const { data: existingLinks } = await supabase
        .from('content_links')
        .select('metadata')
        .match({
          source_id: retroId,
          source_type: 'retrospective',
          target_id: draggedItemData.cardId,
          target_type: targetType
        });

      const existingLink = existingLinks?.[0];
      const existingSections = existingLink?.metadata?.referenced_in_sections || [];

      // Upsert retrospective-level link with proper null handling
      const { error: retroLinkError } = await supabase
        .from('content_links')
        .upsert({
          source_id: retroId,
          source_type: 'retrospective',
          target_id: draggedItemData.cardId,
          target_type: targetType,
          user_id: user.id,
          metadata: {
            referenced_in_sections: [...new Set([...existingSections, dropTargetId])]
          }
        });

      if (retroLinkError) throw retroLinkError;

      // Add to linked content if not already present
      const isAlreadyLinked = linkedContent.some(content => content.id === draggedItemData.cardId);
      if (!isAlreadyLinked) {
        setLinkedContent(prev => [...prev, {
          id: draggedItemData.cardId,
          type: targetType === 'career_card' ? 'career_card' : 'note',
          title: draggedItemData.title,
          preview: draggedItemData.preview || '',
          created_at: new Date().toISOString()
        }]);
      }

      showToast(`${targetType === 'career_card' ? 'Card' : 'Note'} linked successfully`, 'success');
    } catch (error) {
      console.error('Error linking item:', error);
      showToast('Failed to link item', 'error');
    } finally {
      setInvalidDropTargets(new Set());
    }
  };

  const handleUnlinkContent = async (entryId: string, contentId: string, contentType: 'note' | 'card') => {
    if (!user) {
      showToast('You need to be logged in to unlink content', 'error');
      return;
    }
    
    try {
      // Delete the entry-specific content link
      const { error: entryLinkError } = await supabase
        .from('content_links')
        .delete()
        .match({
          source_id: entryId,
          source_type: 'retrospective_entry',
          target_id: contentId,
          target_type: contentType === 'card' ? 'career_card' : 'note'
        });

      if (entryLinkError) throw entryLinkError;

      // Update local entries state immediately
      setEntries(prev => prev.map(entry => {
        if (entry.id === entryId) {
          return {
            ...entry,
            metadata: {
              ...entry.metadata,
              referencedContent: entry.metadata?.referencedContent?.filter(
                ref => !(ref.id === contentId && ref.type === contentType)
              ) || []
            }
          };
        }
        return entry;
      }));

      // Check if this content is still referenced in any other sections
      const { data: remainingEntryLinks } = await supabase
        .from('content_links')
        .select('source_id, metadata')
        .match({
          target_id: contentId,
          target_type: contentType === 'card' ? 'career_card' : 'note',
          source_type: 'retrospective_entry'
        })
        .filter('metadata->retrospective_id', 'eq', retroId);

      // If no other sections reference this content, remove the retrospective-level link
      if (!remainingEntryLinks?.length) {
        const { error: retroLinkError } = await supabase
          .from('content_links')
          .delete()
          .match({
            source_id: retroId,
            source_type: 'retrospective',
            target_id: contentId,
            target_type: contentType === 'card' ? 'career_card' : 'note'
          });

        if (retroLinkError) throw retroLinkError;

        // Also remove from local linkedContent state
        setLinkedContent(prev => prev.filter(content => content.id !== contentId));
      } else {
        // Update the retrospective-level link's metadata to reflect remaining sections
        const remainingSections = remainingEntryLinks.map(link => link.metadata.section_id);
        const { error: updateError } = await supabase
          .from('content_links')
          .update({
            metadata: {
              referenced_in_sections: remainingSections
            }
          })
          .match({
            source_id: retroId,
            source_type: 'retrospective',
            target_id: contentId,
            target_type: contentType === 'card' ? 'career_card' : 'note'
          });

        if (updateError) throw updateError;
      }

      showToast('Content unlinked successfully', 'success');
    } catch (error) {
      console.error('Error unlinking content:', error);
      showToast('Failed to unlink content', 'error');
    }
  };

  // Replace the MDEditor rendering with this:
  const renderMarkdownEditor = () => {
    if (typeof window === 'undefined') {
      return null;
    }
    
    return (
      <ClientOnly fallback={<div className="loading-editor">Loading editor...</div>}>
        <div className="markdown-editor-container">
          <MarkdownEditor
            value={editedDescription}
            onChange={(value) => setEditedDescription(value || '')}
            placeholder="Enter your retrospective description..."
            height={300}
            darkMode={darkMode}
          />
        </div>
      </ClientOnly>
    );
  };

  const renderMarkdownPreview = () => {
    if (typeof window === 'undefined') {
      return null;
    }
    
    return (
      <ClientOnly fallback={<div className="loading-preview">Loading preview...</div>}>
        <div className={`entry-preview-container ${darkMode ? 'dark-preview' : ''}`}>
          <div className="markdown-content p-4" dangerouslySetInnerHTML={{ __html: retro?.description || '' }} />
        </div>
      </ClientOnly>
    );
  };

  // For entry content rendering
  const renderEntryEditor = (entryId: string, content: string, placeholder: string) => {
    if (typeof window === 'undefined') {
      return null;
    }
    
    return (
      <ClientOnly fallback={<div className="loading-editor">Loading editor...</div>}>
        <div className="entry-editor-container">
          <MarkdownEditor
            value={content}
            onChange={(value) => handleEntryEdit(entryId, value || '')}
            placeholder={placeholder}
            height={250}
            darkMode={darkMode}
          />
        </div>
      </ClientOnly>
    );
  };

  const renderEntryPreview = (content: string) => {
    if (typeof window === 'undefined') {
      return null;
    }
    
    return (
      <ClientOnly fallback={<div className="loading-preview">Loading preview...</div>}>
        <div className={`entry-preview-container ${darkMode ? 'dark-preview' : ''}`}>
          <div className="markdown-content p-4" dangerouslySetInnerHTML={{ __html: content }} />
        </div>
      </ClientOnly>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className={`text-lg mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {error}
        </p>
        <button
          onClick={() => navigate('/retrospectives')}
          className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Return to Retrospectives
        </button>
      </div>
    );
  }

  if (!retro) {
    return (
      <div className="text-center py-12">
        <p className={`text-lg mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Retrospective not found
        </p>
        <button
          onClick={() => navigate('/retrospectives')}
          className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Return to Retrospectives
        </button>
      </div>
    );
  }

  // Update to use framework_type instead of framework
  const frameworkType = retro.framework_type || 'custom';
  const frameworkLayout = FRAMEWORK_TEMPLATES[frameworkType as keyof typeof FRAMEWORK_TEMPLATES] || FRAMEWORK_TEMPLATES.custom;

  return (
    <ErrorBoundary>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex h-screen overflow-hidden">
          {/* Side Panel */}
          <RetroSidePanel isOpen={isSidePanelOpen} onClose={() => setIsSidePanelOpen(false)} />

          {/* Main Content */}
          <div 
            ref={mainContentRef} 
            className={`flex-1 overflow-y-auto transition-[margin] duration-300 ease-in-out ${
              isSidePanelOpen 
                ? 'ml-[320px] lg:ml-[60px]' 
                : 'ml-0'
            }`}
          >
            <div className="px-4 py-4 lg:pl-0">
              {/* Navigation */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setIsSidePanelOpen(!isSidePanelOpen)}
                    className={`p-2 rounded-md ${
                      darkMode
                        ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                    }`}
                    aria-label="Toggle side panel"
                  >
                    <IoMenuOutline className="text-xl" />
                  </button>
                  <Link
                    to="/retrospectives"
                    className={`inline-flex items-center gap-2 text-sm ${
                      darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <IoArrowBack className="text-lg" />
                    Back to Retrospectives
                  </Link>
                </div>
                
                <div className="flex items-center gap-2">
                  {isEditMode ? (
                    <>
                      <QuickActionButton
                        label="Cancel"
                        icon={<IoArrowBack className="text-lg" />}
                        onClick={handleEditModeToggle}
                        className="text-gray-500"
                      />
                      <QuickActionButton
                        label="Save Changes"
                        icon={<IoSaveOutline className="text-lg" />}
                        onClick={handleSaveChanges}
                        className={`text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-500 ${
                          isSaving ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      />
                    </>
                  ) : (
                    <>
                      <QuickActionButton
                        label="Edit"
                        icon={<IoPencilOutline className="text-lg" />}
                        onClick={handleEditModeToggle}
                      />
                      <QuickActionButton
                        label="Export"
                        icon={<IoDownloadOutline className="text-lg" />}
                        onClick={handleExport}
                        className={isExporting ? 'opacity-50 cursor-not-allowed' : ''}
                      />
                      <QuickActionButton
                        label="Share"
                        icon={<IoShareOutline className="text-lg" />}
                        onClick={handleShare}
                      />
                      <QuickActionButton
                        label="Delete"
                        icon={<IoTrashOutline className="text-lg" />}
                        onClick={() => setIsDeleteModalOpen(true)}
                        className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-500"
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Header */}
              <div className="mb-8">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {isEditMode && retro ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          className={`w-full text-2xl font-bold px-2 py-1 rounded-md ${
                            darkMode 
                              ? 'bg-gray-700 text-white border-gray-600 focus:border-blue-500' 
                              : 'bg-white text-gray-900 border-gray-300 focus:border-blue-500'
                          } border focus:outline-none focus:ring-1 focus:ring-blue-500`}
                          placeholder="Enter title"
                        />
                        {renderMarkdownEditor()}
                      </div>
                    ) : retro && (
                      <>
                        <h1 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {retro.title}
                        </h1>
                        {renderMarkdownPreview()}
                      </>
                    )}
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm ${
                    darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {(retro.framework_type || 'custom').split('-').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ')}
                  </div>
                </div>
                <div className={`flex flex-wrap gap-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="flex items-center gap-1">
                    <IoCalendarOutline />
                    Created {format(new Date(retro.created_at), 'MMM d, yyyy')}
                  </span>
                  <span>•</span>
                  {isEditMode ? (
                    <div className="flex items-center gap-2">
                      <IoTimeOutline />
                      <input
                        type="date"
                        value={editedStartDate}
                        onChange={(e) => setEditedStartDate(e.target.value)}
                        max={editedEndDate}
                        aria-label="Start date"
                        title="Start date"
                        className={`px-2 py-1 rounded-md text-sm ${
                          darkMode 
                            ? 'bg-gray-700 text-white border-gray-600 focus:border-blue-500 scheme-dark' 
                            : 'bg-white text-gray-900 border-gray-300 focus:border-blue-500 scheme-light'
                        } border focus:outline-none focus:ring-1 focus:ring-blue-500`}
                      />
                      <span>-</span>
                      <input
                        type="date"
                        value={editedEndDate}
                        onChange={(e) => setEditedEndDate(e.target.value)}
                        min={editedStartDate}
                        aria-label="End date"
                        title="End date"
                        className={`px-2 py-1 rounded-md text-sm ${
                          darkMode 
                            ? 'bg-gray-700 text-white border-gray-600 focus:border-blue-500 scheme-dark' 
                            : 'bg-white text-gray-900 border-gray-300 focus:border-blue-500 scheme-light'
                        } border focus:outline-none focus:ring-1 focus:ring-blue-500`}
                      />
                    </div>
                  ) : (
                    <span className="flex items-center gap-1">
                      <IoTimeOutline />
                      {format(new Date(retro.start_date), 'MMM d, yyyy')} - {format(new Date(retro.end_date), 'MMM d, yyyy')}
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="space-y-8" ref={contentRef}>
                {frameworkLayout.sections.map((section: FrameworkSection) => {
                  const sectionEntries = entries.filter(entry => entry.category === section.id);
                  const hasExistingEntry = sectionEntries.length > 0;
                  
                  const displayEntries = hasExistingEntry ? sectionEntries : [{
                    id: `new-${section.id}`,
                    retrospective_id: retroId || '',
                    content: '',
                    category: section.id,
                    created_at: new Date().toISOString()
                  }];

                  return (
                    <div key={section.id} className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {section.label}
                        </h2>
                      </div>
                      <DroppableSection 
                        id={section.id} 
                        darkMode={darkMode}
                        isInvalid={invalidDropTargets.has(section.id)}
                      >
                        <div className="grid grid-cols-1 gap-4">
                          {displayEntries.map(entry => (
                            <div
                              key={entry.id}
                              className={`p-4 rounded-lg transition-all duration-200 ${
                                darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'
                              }`}
                            >
                              <div className="prose dark:prose-invert max-w-none">
                                {isEditMode ? (
                                  <div data-color-mode={darkMode ? "dark" : "light"}>
                                    {renderEntryEditor(
                                      entry.id, 
                                      editedEntries[entry.id] !== undefined ? editedEntries[entry.id] : entry.content || '',
                                      section.placeholder || `Write your ${section.label.toLowerCase()} here...`
                                    )}
                                  </div>
                                ) : (
                                  <div data-color-mode={darkMode ? "dark" : "light"}>
                                    {renderEntryPreview(entry.content || '')}
                                  </div>
                                )}
                              </div>
                              {/* Always show Referenced Content area */}
                              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Referenced content:
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {entry.metadata?.referencedContent?.map(ref => {
                                    const content = linkedContent.find(c => c.id === ref.id);
                                    if (!content) return null;
                                    return (
                                      <div
                                        key={ref.id}
                                        className={`group flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-colors duration-200 ${
                                          ref.type === 'note'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                            : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                        } ${activeContentId === ref.id ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                                      >
                                        <button
                                          onClick={() => handleContentClick(ref.id)}
                                          className="hover:underline"
                                        >
                                          {content.title}
                                        </button>
                                        <button
                                          onClick={() => handleUnlinkContent(entry.id, ref.id, ref.type)}
                                          className="opacity-0 group-hover:opacity-100 ml-1 hover:text-red-600 dark:hover:text-red-400"
                                          title="Unlink content"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    );
                                  })}
                                  {(!entry.metadata?.referencedContent || entry.metadata.referencedContent.length === 0) && (
                                    <p className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Drag and drop items here to link them
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </DroppableSection>
                    </div>
                  );
                })}
              </div>

              {/* Linked Content */}
              {linkedContent.length > 0 && (
                <div className="mt-12">
                  <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Referenced Content
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {linkedContent.map(content => (
                      <div
                        key={content.id}
                        className={`transition-all duration-200 ${
                          activeContentId === content.id ? 'ring-2 ring-blue-500 rounded-lg' : ''
                        }`}
                      >
                        <LinkedContentCard content={content} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Delete Confirmation Modal */}
              <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
              >
                <div className="p-6">
                  <h3 className={`text-lg font-medium mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Delete Retrospective
                  </h3>
                  <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Are you sure you want to delete this retrospective? This action cannot be undone.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setIsDeleteModalOpen(false)}
                      className={`px-4 py-2 rounded-md ${
                        darkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      className="px-4 py-2 rounded-md bg-red-500 text-white hover:bg-red-600"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </Modal>

              {/* Share Modal */}
              <Modal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
              >
                <div className="p-6">
                  <h3 className={`text-lg font-medium mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Share Retrospective
                  </h3>
                  <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Share this retrospective with others:
                  </p>
                  <div className="flex gap-3 mb-6">
                    <div className="flex-1">
                      <label htmlFor="share-url" className="sr-only">Share URL</label>
                      <input
                        id="share-url"
                        type="text"
                        readOnly
                        value={`${window.location.origin}/retrospectives/${retroId}`}
                        aria-label="Share URL"
                        className={`w-full px-3 py-2 rounded-md ${
                          darkMode
                            ? 'bg-gray-700 text-gray-300 border-gray-600'
                            : 'bg-gray-50 text-gray-900 border-gray-200'
                        } border`}
                      />
                    </div>
                    <button
                      onClick={handleCopyLink}
                      className={`px-4 py-2 rounded-md ${
                        darkMode
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      Copy Link
                    </button>
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() => setIsShareModalOpen(false)}
                      className={`px-4 py-2 rounded-md ${
                        darkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </Modal>
            </div>
          </div>
        </div>

        {/* Drag Overlay */}
        <DragOverlay dropAnimation={null}>
          {activeDragItem && !isDropSuccessful && (
            <div className={`px-3 py-1.5 rounded-full text-sm font-medium shadow-sm ${
              darkMode 
                ? activeDragItem.type === 'note'
                  ? 'bg-green-900 text-green-100'
                  : 'bg-purple-900 text-purple-100'
                : activeDragItem.type === 'note'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-purple-100 text-purple-800'
            }`}>
              {activeDragItem.title}
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </ErrorBoundary>
  );
});